/**
 * Date Helper Functions
 * Common date parsing, formatting, and validation utilities for gemengApp modules
 */

/**
 * Parse date string in d-m-Y format
 * @param {string} dateStr - Date string in format "DD-MM-YYYY"
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export function parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    return null;
}

/**
 * Parse date string in DD-MM-YYYY format specifically
 * @param {string} dateStr - Date string in format "DD-MM-YYYY"
 * @returns {Date} - JavaScript Date object
 */
export function parseDDMMYYYY(dateStr) {
    const [dd, mm, yyyy] = dateStr.split('-');
    return new Date(`${yyyy}-${mm}-${dd}`);
}

/**
 * Format date to DD-MM-YYYY display format
 * @param {Date} date - JavaScript Date object
 * @returns {string} - Formatted date string
 */
export function formatDate(date) {
    return date.getDate().toString().padStart(2, '0') + '-' +
        (date.getMonth() + 1).toString().padStart(2, '0') + '-' +
        date.getFullYear();
}

/**
 * Format date string from YYYY-MM-DD to DD-MM-YYYY
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Date string in DD-MM-YYYY format
 */
export function formatDateToDMY(dateStr) {
    if (!dateStr || dateStr.length !== 10) return '';
    const [yyyy, mm, dd] = dateStr.split('-');
    return `${dd}-${mm}-${yyyy}`;
}

/**
 * Format date for FullCalendar - supports both french and english formats
 * @param {Date} date - JavaScript Date object
 * @param {string} type - 'french' or 'english' format
 * @returns {Object} - Object with date and time strings
 */
export function formatDateGemengApp(date, type = 'french') {
    const pad = (num) => num.toString().padStart(2, '0');
    if (type === 'french') {
        return {
            date: `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`,
            time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
        };
    } else {
        return {
            date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
            time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
        };
    }
}

/**
 * Parse date string in gemengApp format (DD-MM-YYYY)
 * @param {string} dateStr - Date string in DD-MM-YYYY format
 * @returns {Date} - JavaScript Date object
 */
export function parseDateGemengApp(dateStr) {
    const [day, month, year] = dateStr.split("-");
    return new Date(year, month - 1, day);
}

/**
 * Get current date as formatted string
 * @returns {string} - Current date in DD-MM-YYYY format
 */
export function getCurrentDateFormatted() {
    const today = new Date();
    return today.getDate().toString().padStart(2, '0') + '-' +
        (today.getMonth() + 1).toString().padStart(2, '0') + '-' +
        today.getFullYear();
}

/**
 * Get minimum selectable date based on minDayBooking constraint
 * @param {number} minDayBooking - Minimum days from today
 * @returns {Date} - Minimum selectable date
 */
export function getMinSelectableDate(minDayBooking = 4) {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + minDayBooking);
    return minDate;
}

/**
 * Check if two date ranges overlap
 * @param {Date} startDate - Start date of range
 * @param {Date} endDate - End date of range
 * @param {Object} bookedDays - Object with full and partial bookings
 * @returns {Array} - Array of overlapping dates
 */
export function checkDateRangeOverlap(startDate, endDate, bookedDays) {
    const overlapDates = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
        // Use YYYY-MM-DD format for logic
        const dateStr = current.getFullYear() + '-' + 
            (current.getMonth() + 1).toString().padStart(2, '0') + '-' + 
            current.getDate().toString().padStart(2, '0');
        // Use DD-MM-YYYY for display
        const formattedDate = formatDate(current);
        
        if (bookedDays.full.includes(dateStr)) {
            overlapDates.push({type: 'full', date: formattedDate});
        } else {
            // Find partial booking for this date
            const partialObj = bookedDays.partial.find(p => p.date === dateStr);
            if (partialObj) {
                overlapDates.push({
                    type: 'partial',
                    date: formattedDate,
                    startTime: partialObj.startTime,
                    endTime: partialObj.endTime
                });
            }
        }
        current.setDate(current.getDate() + 1);
    }
    
    return overlapDates;
}

// Horizon de réservation : on ouvre jusqu'au 31 décembre de l'année courante + 3.
// Les salles se réservent par « saison » (fêtes, kermesses), donc la borne est une
// fin d'année civile et non une date glissante.
//
// Cette constante est le point de vérité PARTAGÉ entre les pickers du formulaire
// (flatpickr-init-app.js) et le calendrier de la fiche salle (hallBooking/_calendar.js).
// Tant qu'elle vivait dans le seul formulaire, la fiche laissait naviguer sans limite :
// on consultait 2034 avant de découvrir dans le formulaire qu'on ne pouvait pas réserver.
export const BOOKING_HORIZON_YEARS = 3;

/** Dernier jour réservable (31/12 de l'année courante + BOOKING_HORIZON_YEARS). */
export function bookingHorizonEnd() {
    const t = new Date();
    return new Date(t.getFullYear() + BOOKING_HORIZON_YEARS, 11, 31);
}

/** Borne de fin EXCLUSIVE, pour les API qui en attendent une (FullCalendar validRange). */
export function bookingHorizonEndExclusive() {
    const t = new Date();
    return new Date(t.getFullYear() + BOOKING_HORIZON_YEARS + 1, 0, 1);
}

/**
 * Noms de jours et de mois en luxembourgeois.
 *
 * Le luxembourgeois ne peut PAS passer par `Intl` : `lb` et `lb-LU` ne sont pas dans
 * l'ICU de Chrome (`supportedLocalesOf` rend une liste vide) et `toLocaleDateString`
 * retombe alors sans prévenir sur la locale du NAVIGATEUR — un visiteur configuré en
 * anglais lirait une date anglaise au milieu d'une page luxembourgeoise. Les noms sont
 * donc écrits ici, une fois, et servent à la fois au récapitulatif du formulaire et à
 * la locale flatpickr construite à la main (les calendriers restaient en français sur
 * /lu/, faute de locale `lb` publiée par flatpickr).
 */
export const JOURS_LU = ['Sonndeg', 'Méindeg', 'Dënschdeg', 'Mëttwoch', 'Donneschdeg', 'Freideg', 'Samschdeg'];
export const JOURS_LU_COURTS = ['So', 'Mé', 'Dë', 'Më', 'Do', 'Fr', 'Sa'];
export const MOIS_LU = ['Januar', 'Februar', 'Mäerz', 'Abrëll', 'Mee', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
export const MOIS_LU_COURTS = ['Jan', 'Feb', 'Mäe', 'Abr', 'Mee', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/**
 * Langue de la page, sur deux lettres. Renseignée par `layout.njk` (`lang="{{ locale.slug }}"`).
 */
export function langueDeLaPage() {
    return (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
}
